import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';
import { DraggableColumnCard } from '../DraggableCard';
import { Card } from '../../Atoms/Card';
import { Toggle } from '../../Atoms/Toggle';
import { Text, TextSpan } from '../../Atoms/Text';
import { arrayMove } from '../../../utils/helpers';
import { DefaultInventoryGridColumnDefs } from '../../Pages/Inventory/inventoryGridColumnDefs';
import { GroupedGridColDef } from '../UpstackDataGrid/gridColumnTypes';
import { GridPreferences } from '../../../store/User';
import { completeColumnOrder, sortByOrderedFields } from '../UpstackDataGrid/columnOrder';
import { isFieldVisible } from '../UpstackDataGrid/columnVisibility';
import { isCustomFieldColumn } from '../UpstackDataGrid/customFieldColumns';

// Marks a column that comes from an account's custom field definitions rather than the built-in catalog.
const CustomFieldBadge = () => (
  <TextSpan
    size="xs"
    color="grey9"
    weight="medium"
    className="ml-2 rounded bg-grey-2 px-1.5 py-0.5">
    Custom
  </TextSpan>
);

export interface CustomizeTableColumnsProps {
  gridPreferences: GridPreferences;
  setGridPreferences: Dispatch<SetStateAction<GridPreferences>>;
  type: string;
  columnDefs?: GroupedGridColDef[];
  layout?: 'list' | 'twoPane';
}

// Figma's two-pane rows are flush (no border/shadow/gap) and stripe white/grey by position.
const zebraRowClassName = (index: number, extra = '') =>
  `flex items-center p-3 ${index % 2 === 0 ? 'bg-grey-0' : 'bg-grey-1'} ${extra}`.trim();

interface AvailableColumnRowProps {
  field: string;
  headerName: string;
  index: number;
  onAdd: () => void;
}

const AvailableColumnRow = ({ field, headerName, index, onAdd }: AvailableColumnRowProps) => (
  <Card
    className={zebraRowClassName(index, 'justify-between')}
    enableShadow={false}
    useDefaultCardStyles={false}>
    <span className="flex items-center">
      <Text
        size="sm"
        dataTestId={headerName}>
        {headerName}
      </Text>
      {isCustomFieldColumn(field) && <CustomFieldBadge />}
    </span>
    <button
      type="button"
      data-testid={`${field}-add-button`}
      className="text-main-100 text-sm font-medium"
      onClick={onAdd}>
      + Add
    </button>
  </Card>
);

const getAllChecked = (gridPreferences: GridPreferences) => {
  const visibilityModel = gridPreferences?.muiConfig?.columns?.columnVisibilityModel;
  return Object.keys(visibilityModel || {}).every((k) => visibilityModel?.[k]);
};

const isCustomizableField = (field: string, disabledFields: string[]) =>
  !disabledFields.includes(field) && !['details', 'detailCTA'].includes(field);

const getRenderedColumns = (columnDefs: GroupedGridColDef[], disabledFields: string[], type: string) =>
  columnDefs.filter((col) => isCustomizableField(col.field, type === 'user' ? disabledFields : []));

// getRenderedColumns has already left out what a mode may not offer, so only the layout decides here.
const isColumnVisible = (col: GroupedGridColDef, visibilityModel?: Record<string, boolean>) =>
  isFieldVisible(col.field, col.defaultHidden, [], visibilityModel);

// The cards in the grid's order: the saved order, completed with the catalog columns it lacks.
const orderedColumnsFor = (gridPreferences: GridPreferences, columnDefs: GroupedGridColDef[], type: string) =>
  sortByOrderedFields(
    getRenderedColumns(columnDefs, gridPreferences.disabledFields, type),
    completeColumnOrder(gridPreferences.muiConfig?.columns?.orderedFields || [], columnDefs)
  );

export const CustomizeTableColumns = ({
  gridPreferences,
  setGridPreferences,
  type,
  columnDefs = DefaultInventoryGridColumnDefs,
  layout = 'list'
}: CustomizeTableColumnsProps) => {
  const [columns, setColumns] = useState<GroupedGridColDef[]>(() =>
    orderedColumnsFor(gridPreferences, columnDefs, type)
  );
  const [loading, setLoading] = useState(false);
  const [toggleAllChecked, setToggleAllChecked] = useState(getAllChecked(gridPreferences));

  useEffect(() => {
    const { disabledFields, muiConfig } = gridPreferences;
    const visibilityModel = muiConfig?.columns?.columnVisibilityModel || {};
    const customizableColumns = columnDefs.filter((col) => isCustomizableField(col.field, disabledFields));

    // An account's empty layout leaves each column to the catalog; filling it would show off-by-default columns to all.
    if (type !== 'account' && !Object.keys(visibilityModel).length) {
      customizableColumns.forEach(({ field }) => {
        visibilityModel[field] = true;
      });
    }

    if (muiConfig?.columns && !muiConfig.columns.orderedFields?.length) {
      muiConfig.columns.orderedFields = customizableColumns.map(({ field }) => field);
    }

    setColumns(orderedColumnsFor(gridPreferences, columnDefs, type));
    setToggleAllChecked(getAllChecked(gridPreferences));
  }, [gridPreferences, columnDefs, type]);

  const visibilityModel = gridPreferences?.muiConfig?.columns?.columnVisibilityModel;
  const visibleColumns = columns.filter((col) => isColumnVisible(col, visibilityModel));
  const hiddenColumns = columns.filter((col) => !isColumnVisible(col, visibilityModel));

  const handleOnDragEnd = (draggedCard: DropResult) => {
    if (!draggedCard.destination) return;
    if (draggedCard.source.index === draggedCard.destination.index) return;

    setLoading(true);

    const draggableColumns = layout === 'twoPane' ? visibleColumns : columns;
    const draggedField = draggableColumns[draggedCard.source.index]?.field;
    const droppedOnField = draggableColumns[draggedCard.destination.index]?.field;

    const order = completeColumnOrder(gridPreferences?.muiConfig?.columns?.orderedFields || [], columnDefs);
    const fromIndex = order.indexOf(draggedField);
    const toIndex = order.indexOf(droppedOnField);
    if (fromIndex !== -1 && toIndex !== -1) arrayMove(order, fromIndex, toIndex);

    const update: GridPreferences = {
      ...gridPreferences,
      muiConfig: {
        ...gridPreferences.muiConfig,
        columns: {
          ...gridPreferences.muiConfig?.columns,
          orderedFields: order
        }
      }
    };
    setGridPreferences(update);

    setColumns(sortByOrderedFields(columns, order));
    setToggleAllChecked(getAllChecked(update));
    setLoading(false);
  };

  const handleColumnVisibilityChange = (colId: string, visible: boolean) => {
    setLoading(true);

    const prefUpdate = { ...gridPreferences };
    const visibilityModel = prefUpdate?.muiConfig?.columns?.columnVisibilityModel;
    if (visibilityModel) {
      visibilityModel[colId] = visible;
      setGridPreferences(prefUpdate);
    }
    setLoading(false);
  };

  const handleToggleAll = () => {
    setLoading(true);

    const prefUpdate = { ...gridPreferences };
    const visibilityModel = prefUpdate?.muiConfig?.columns?.columnVisibilityModel;

    columns.forEach(({ field }: GroupedGridColDef) => {
      if (visibilityModel) visibilityModel[field] = !toggleAllChecked;
    });
    setGridPreferences(prefUpdate);
    setToggleAllChecked(!toggleAllChecked);

    setLoading(false);
  };

  return (
    <>
      {layout === 'list' && (
        <div className="pb-1 border-b border-grey-4 text-end items-center">
          <label
            className="space-x-2"
            htmlFor="toggle-all">
            <Text
              size="sm"
              className="text-gray-700"
              dataTestId="all_fields"
              isSpan>
              Select All Fields
            </Text>
            <Toggle
              id="toggle-all"
              className="inline-block"
              checked={toggleAllChecked}
              onChange={() => handleToggleAll()}
              width={7}
              height={4}
            />
          </label>
        </div>
      )}
      {!loading && layout === 'list' && (
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="columns">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="h-[24rem] w-[24rem] grow flex flex-col mt-4"
                data-testid="menu-group">
                {columns.map((col, index) => {
                  const { headerName, field } = col;
                  const hide = !isColumnVisible(col, visibilityModel);
                  return (
                    <DraggableColumnCard
                      key={field}
                      colId={field}
                      index={index}
                      headerName={headerName as string}
                      hide={hide}
                      setColumnVisible={(key, visible) => handleColumnVisibilityChange(key, visible)}
                    />
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
      {!loading && layout === 'twoPane' && (
        <div className="flex gap-6 mt-4">
          <div
            className="flex flex-col grow basis-0"
            data-testid="available-columns-pane">
            <Text
              size="sm"
              weight="medium"
              className="text-gray-700 mb-2"
              isSpan>
              Hidden Columns
            </Text>
            {hiddenColumns.map(({ headerName, field }, index) => (
              <AvailableColumnRow
                key={field}
                field={field}
                headerName={headerName as string}
                index={index}
                onAdd={() => handleColumnVisibilityChange(field, true)}
              />
            ))}
          </div>
          <div
            className="flex flex-col grow basis-0"
            data-testid="visible-columns-pane">
            <Text
              size="sm"
              weight="medium"
              className="text-gray-700 mb-2"
              isSpan>
              Default Visible Columns (drag to reorder)
            </Text>
            <DragDropContext onDragEnd={handleOnDragEnd}>
              <Droppable droppableId="visible-columns">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    data-testid="menu-group">
                    {visibleColumns.map(({ headerName, field }, index) => (
                      <DraggableColumnCard
                        key={field}
                        colId={field}
                        index={index}
                        headerName={headerName as string}
                        hide={false}
                        setColumnVisible={(key, visible) => handleColumnVisibilityChange(key, visible)}
                        variant="remove"
                        badge={isCustomFieldColumn(field) ? <CustomFieldBadge /> : undefined}
                        cardClassName={zebraRowClassName(index)}
                        enableCardShadow={false}
                        useDefaultCardStyles={false}
                      />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>
      )}
    </>
  );
};
